import React, { useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { TextStyle, Color, FontFamily } from "@tiptap/extension-text-style";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Placeholder from "@tiptap/extension-placeholder";
import { Extension } from "@tiptap/core";
import { Plugin } from "prosemirror-state";
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  StrikethroughIcon,
  LinkIcon,
  XMarkIcon,
  ListBulletIcon,
  NumberedListIcon,
} from "@heroicons/react/24/outline";

function isSafeHttpUrl(raw) {
  try {
    const url = new URL(raw.trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function normalizeUrl(raw) {
  const value = (raw || "").trim();
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return `https://${value}`;
}

function clampFontSize(value) {
  const sizes = ["12px", "14px", "16px", "18px", "20px", "24px", "28px"];
  return sizes.includes(value) ? value : "16px";
}

function clampNumber(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function CheckBoxIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="m8 12 2.5 2.5L16 9" />
    </svg>
  );
}

function computePopoverPos(containerRef, buttonRef, width) {
  const container = containerRef.current;
  const button = buttonRef.current;
  if (!container || !button) return null;
  const c = container.getBoundingClientRect();
  const b = button.getBoundingClientRect();
  const top = b.bottom - c.top + 8;
  const leftRaw = b.left - c.left;
  const maxLeft = c.width - width - 12;
  const left = clampNumber(leftRaw, 12, Math.max(12, maxLeft));
  return { top, left };
}

const PlainPaste = Extension.create({
  name: "plainPaste",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handlePaste: (_view, event) => {
            const text = event.clipboardData?.getData("text/plain");
            if (!text) return false;
            event.preventDefault();
            const cleaned = text.replace(/\r\n/g, "\n").replace(/\u00A0/g, " ");
            this.editor.commands.insertContent(cleaned);
            return true;
          },
        },
      }),
    ];
  },
});

const FontSizeExtension = Extension.create({
  name: "fontSize",
  addGlobalAttributes() {
    return [
      {
        types: ["textStyle"],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attrs) =>
              attrs.fontSize
                ? { style: `font-size: ${clampFontSize(attrs.fontSize)}` }
                : {},
          },
        },
      },
    ];
  },
});

function ToolbarButton({ active, disabled, onClick, title, children }) {
  const className = [
    "h-8 w-8 grid place-items-center rounded-md border",
    disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-50",
    active ? "border-gray-300 bg-gray-100" : "border-gray-200 bg-white",
  ].join(" ");
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={className}
      title={title}
    >
      {children}
    </button>
  );
}

function LinkPopover({
  isOpen,
  pos,
  value,
  onChange,
  onApply,
  onRemove,
  onClose,
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onDown = (event) => {
      if (ref.current?.contains(event.target)) return;
      onClose();
    };
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !pos) return null;

  return (
    <div
      ref={ref}
      className="absolute z-50 w-[360px] rounded-xl border border-gray-200 bg-white shadow-lg p-3"
      style={{ top: pos.top, left: pos.left }}
    >
      <div className="text-sm font-medium text-gray-900 mb-2">Lien</div>

      <input
        className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-gray-900 outline-none focus:border-gray-300"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onApply();
          if (e.key === "Escape") onClose();
        }}
        autoFocus
      />

      <div className="flex items-center justify-between mt-3">
        <button
          type="button"
          className="text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
          onClick={onRemove}
        >
          Retirer
        </button>

        <div className="flex gap-2">
          <button
            type="button"
            className="text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
            onClick={onClose}
          >
            Annuler
          </button>
          <button
            type="button"
            className="text-sm px-3 py-2 rounded-lg border border-gray-300 bg-gray-900 text-white hover:bg-gray-800"
            onClick={onApply}
          >
            Appliquer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StylizedEditor({
  label,
  valueJson,
  onChangeJson,
  fullHeight,
}) {
  const allowedFonts = useMemo(() => ["Inter", "Sora", "Arial", "Georgia"], []);
  const containerRef = useRef(null);
  const linkButtonRef = useRef(null);
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [linkValue, setLinkValue] = useState("https://");
  const [linkPos, setLinkPos] = useState(null);

  const defaultContent = useMemo(() => {
    if (!valueJson) return "";
    try {
      return JSON.parse(valueJson);
    } catch {
      return valueJson;
    }
  }, [valueJson]);

  const editor = useEditor({
    content: defaultContent,
    editorProps: {
      attributes: {
        class: [
          "w-full bg-white text-gray-900 outline-none px-3 py-2",
          fullHeight ? "h-full min-h-0 overflow-auto pb-24" : "min-h-[160px]",
        ].join(" "),
      },
    },
    extensions: [
      PlainPaste,
      StarterKit.configure({
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Underline,
      TextStyle,
      Color,
      FontFamily.configure({ types: ["textStyle"] }),
      FontSizeExtension,
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({
        placeholder:
          "Écris ton actualité… (essaie -, 1., ou - [ ] pour listes/checklist)",
      }),
      Link.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
        validate: (href) => isSafeHttpUrl(href),
        HTMLAttributes: {
          rel: "noopener noreferrer nofollow",
          target: "_blank",
        },
      }),
    ],
    onUpdate: ({ editor }) => onChangeJson?.(JSON.stringify(editor.getJSON())),
  });

  const toggleListAtCursor = (type) => {
    if (!editor) return;

    const { from, to } = editor.state.selection;
    if (from !== to) editor.commands.setTextSelection(from);

    if (type === "bullet") editor.chain().focus().toggleBulletList().run();
    if (type === "ordered") editor.chain().focus().toggleOrderedList().run();
    if (type === "task") editor.chain().focus().toggleTaskList().run();
  };

  const openLinkPopover = () => {
    if (!editor) return;
    const current = editor.getAttributes("link").href || "";
    setLinkValue(current ? current : "https://");
    setLinkPos(computePopoverPos(containerRef, linkButtonRef, 360));
    setIsLinkOpen(true);
  };

  const closeLinkPopover = () => setIsLinkOpen(false);

  const applyLink = () => {
    if (!editor) return;
    const url = normalizeUrl(linkValue);
    if (!url) {
      editor.chain().focus().unsetLink().run();
      return closeLinkPopover();
    }
    if (!isSafeHttpUrl(url)) return;
    editor.chain().focus().setLink({ href: url }).run();
    closeLinkPopover();
  };

  const removeLink = () => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
    closeLinkPopover();
  };

  const setFont = (font) => {
    if (!editor || !allowedFonts.includes(font)) return;
    editor.chain().focus().setFontFamily(font).run();
  };

  const setFontSize = (fontSize) => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .setMark("textStyle", { fontSize: clampFontSize(fontSize) })
      .run();
  };

  if (!editor) return null;

  return (
    <div ref={containerRef} className="relative w-full h-full stylized-editor">
      {label ? (
        <div className="text-sm font-medium mb-2 text-gray-900">{label}</div>
      ) : null}

      <div className="rounded-xl border border-gray-200 bg-white h-full flex flex-col overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 p-2 border-b border-gray-200 bg-white">
          <select
            className="h-8 px-2 rounded-md border border-gray-200 bg-white text-sm text-gray-900"
            defaultValue=""
            onChange={(event) => setFont(event.target.value)}
          >
            <option value="" disabled>
              Font
            </option>
            {allowedFonts.map((font) => (
              <option key={font} value={font}>
                {font}
              </option>
            ))}
          </select>

          <select
            className="h-8 px-2 rounded-md border border-gray-200 bg-white text-sm text-gray-900"
            defaultValue="16px"
            onChange={(event) => setFontSize(event.target.value)}
          >
            {["12px", "14px", "16px", "18px", "20px", "24px", "28px"].map(
              (size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              )
            )}
          </select>

          <input
            type="color"
            className="h-8 w-10 rounded-md border border-gray-200 bg-white"
            onChange={(event) =>
              editor.chain().focus().setColor(event.target.value).run()
            }
            title="Couleur"
          />

          <ToolbarButton
            title="Gras"
            active={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <BoldIcon className="h-4 w-4 text-gray-900" />
          </ToolbarButton>

          <ToolbarButton
            title="Italique"
            active={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <ItalicIcon className="h-4 w-4 text-gray-900" />
          </ToolbarButton>

          <ToolbarButton
            title="Souligné"
            active={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon className="h-4 w-4 text-gray-900" />
          </ToolbarButton>

          <ToolbarButton
            title="Barré"
            active={editor.isActive("strike")}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <StrikethroughIcon className="h-4 w-4 text-gray-900" />
          </ToolbarButton>

          <ToolbarButton
            title="Liste à puces"
            active={editor.isActive("bulletList")}
            onClick={() => toggleListAtCursor("bullet")}
          >
            <ListBulletIcon className="h-4 w-4 text-gray-900" />
          </ToolbarButton>

          <ToolbarButton
            title="Liste numérotée"
            active={editor.isActive("orderedList")}
            onClick={() => toggleListAtCursor("ordered")}
          >
            <NumberedListIcon className="h-4 w-4 text-gray-900" />
          </ToolbarButton>

          <ToolbarButton
            title="Checklist"
            active={editor.isActive("taskList")}
            onClick={() => toggleListAtCursor("task")}
          >
            <CheckBoxIcon className="h-4 w-4 text-gray-900" />
          </ToolbarButton>

          <button
            ref={linkButtonRef}
            type="button"
            onClick={openLinkPopover}
            className={[
              "h-8 w-8 grid place-items-center rounded-md border",
              editor.isActive("link")
                ? "border-gray-300 bg-gray-100"
                : "border-gray-200 bg-white hover:bg-gray-50",
            ].join(" ")}
            title="Ajouter / modifier un lien"
          >
            <LinkIcon className="h-4 w-4 text-gray-900" />
          </button>

          <ToolbarButton
            title="Retirer le lien"
            disabled={!editor.isActive("link")}
            onClick={() => editor.chain().focus().unsetLink().run()}
          >
            <XMarkIcon className="h-4 w-4 text-gray-900" />
          </ToolbarButton>
        </div>

        <div className="flex-1 min-h-0 bg-white">
          <EditorContent editor={editor} className="h-full" />
        </div>
      </div>

      <LinkPopover
        isOpen={isLinkOpen}
        pos={linkPos}
        value={linkValue}
        onChange={setLinkValue}
        onApply={applyLink}
        onRemove={removeLink}
        onClose={closeLinkPopover}
      />
    </div>
  );
}
